import { css } from 'lit';

export const styles = css`
	:host {
		--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-button-border-color: var(--sds-button-border-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-button-border-width: var(--sds-button-border-width, var(--sds-border-md));
		--_sds-button-disabled-opacity: var(--sds-button-disabled-opacity, var(--sds-color-states-disabled-opacity));
		--_sds-button-focus-color: var(--sds-button-focus-color, var(--sds-color-states-focus-outer));
		--_sds-button-focus-inner-color: var(--sds-button-focus-inner-color, var(--sds-color-states-focus-inner));
		--_sds-button-focus-spacing: var(--sds-button-focus-spacing, var(--sds-color-states-focus-spacing));
		--_sds-button-font-family: var(--sds-button-font, var(--sds-typography-font-family-roboto));
		--_sds-button-font-size: var(--sds-button-font-size, var(--sds-components-font-size-content-lg));
		--_sds-button-font-weight: var(--sds-button-font-weight, var(--sds-typography-body-md-strong-desktop-font-weight));
		--_sds-button-gap: var(--sds-button-gap, var(--sds-spacing-3xs));
		--_sds-button-line-height: var(--sds-button-line-height, var(--sds-typography-body-md-desktop-line-height));
		--_sds-button-min-height: var(--sds-button-height, var(--sds-components-button-lg));
		--_sds-button-padding-block: var(--sds-button-padding-block, 0);
		--_sds-button-padding-inline: var(--sds-button-padding-inline, var(--sds-spacing-xs));
		--_sds-button-radius: var(--sds-button-radius, var(--sds-radius-sm));
		--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-action-contrast-default, var(--sds-color-main-action-contrast-default)));

		--_sds-button-tertiary-text-decoration: var(--sds-typography-link-text-decoration, underline);
		--_sds-button-tertiary-text-underline-offset: var(--sds-typography-link-text-underline-offset, 4px);
		--_sds-button-tertiary-text-underline-thickness: 2px;

		display: inline-block;
		box-sizing: border-box;

		::slotted(sds-icon) {
			--sds-icon-color: var(--_sds-button-text-color);
		}
	}

	.sds-button {
		align-items: center;
		background-color: var(--_sds-button-background-color);
		border-radius: var(--_sds-button-radius);
		border: var(--_sds-button-border-width) solid var(--_sds-button-border-color);
		color: var(--_sds-button-text-color);
		cursor: pointer;
		display: inline-flex;
		font-family: var(--_sds-button-font-family);
		font-size: var(--_sds-button-font-size);
		font-weight: var(--_sds-button-font-weight);
		gap: var(--_sds-button-gap);
		inline-size: 100%;
		justify-content: center;
		line-height: var(--_sds-button-line-height);
		min-height: var(--_sds-button-min-height);
		padding: var(--_sds-button-padding-block) var(--_sds-button-padding-inline);
		text-align: center;

		&:hover {
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
			--_sds-button-border-color: var(--sds-button-border-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
		}

		&:focus-visible {
			box-shadow: 0 0 0 var(--sds-color-states-focus-spacing) var(--_sds-button-focus-inner-color);
			outline: 3px solid var(--_sds-button-focus-color);
			outline-offset: 3px;
		}

		&:active {
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
			--_sds-button-border-color: var(--sds-button-border-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
		}

		&:disabled {
			opacity: var(--_sds-button-disabled-opacity);
			cursor: not-allowed;
			pointer-events: none;
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-neutral-action-default));
			--_sds-button-border-color: var(--sds-button-border-color, var(--sds-color-neutral-action-default));
			--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-neutral-action-contrast-default));
		}
	}

	.sds-button--as-link {
		box-sizing: border-box;
		inline-size: auto;
		text-decoration: none;
	}

	:host([variant='secondary']) {
		--_sds-button-background-color: var(--sds-button-background-color, transparent);
		--_sds-button-border-color: var(--sds-button-border-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));

		.sds-button:hover {
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-surface-hover, var(--sds-color-main-surface-hover)));
		}

		.sds-button:active {
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-surface-active, var(--sds-color-main-surface-active)));
		}

		.sds-button:disabled {
			--_sds-button-background-color: var(--sds-button-background-color, transparent);
			--_sds-button-border-color: var(--sds-button-border-color, var(--sds-color-neutral-action-default));
			--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-neutral-action-default));
		}
	}

	:host([variant='tertiary']) {
		--_sds-button-background-color: var(--sds-button-background-color, transparent);
		--_sds-button-border-color: var(--sds-button-border-color, transparent);
		--_sds-button-padding-inline: var(--sds-button-padding-inline, 0);
		--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));

		.sds-button:hover {
			--_sds-button-background-color: var(--sds-button-background-color, transparent);
			--_sds-button-border-color: var(--sds-button-border-color, transparent);
			--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));

			text-decoration: var(--_sds-button-tertiary-text-decoration);
			text-decoration-thickness: var(--_sds-button-tertiary-text-underline-thickness);
			text-underline-offset: var(--_sds-button-tertiary-text-underline-offset);
		}

		.sds-button:active {
			--_sds-button-background-color: var(--sds-button-background-color, transparent);
			--_sds-button-border-color: var(--sds-button-border-color, transparent);
			--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
		}

		.sds-button:disabled {
			--_sds-button-background-color: var(--sds-button-background-color, transparent);
			--_sds-button-border-color: var(--sds-button-border-color, transparent);
			--_sds-button-text-color: var(--sds-button-text-color, var(--sds-color-neutral-action-default));
		}
	}

	:host([variant='tertiary']):host([icon]) {
		.sds-button:hover {
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-surface-hover, var(--sds-color-main-surface-hover)));
			--_sds-button-border-color: var(--sds-button-border-color, transparent);
		}

		.sds-button:active {
			--_sds-button-background-color: var(--sds-button-background-color, var(--sds-color-surface-active, var(--sds-color-main-surface-active)));
			--_sds-button-border-color: var(--sds-button-border-color, transparent);
		}
	}

	:host([size='medium']), /* Deprecated */
	:host([size='md']) {
		--_sds-button-min-height: var(--sds-button-height, var(--sds-components-button-md));
		--_sds-button-padding-block: var(--sds-button-padding-block, var(--sds-spacing-4xs));
		--_sds-button-padding-inline: var(--sds-button-padding-inline, var(--sds-spacing-3xs));
		--_sds-button-font-size: var(--sds-button-font-size, var(--sds-components-font-size-content-md));

		&:host([variant='tertiary']) {
			--_sds-button-padding-inline: var(--sds-button-padding-inline, 0);
		}
	}

	:host([size='small']), /* Deprecated */
	:host([size='sm']) {
		--_sds-button-min-height: var(--sds-button-height, var(--sds-components-button-sm));
		--_sds-button-padding-block: var(--sds-button-padding-block, 0);
		--_sds-button-padding-inline: var(--sds-button-padding-inline, var(--sds-spacing-3xs));
		--_sds-button-font-size: var(--sds-button-font-size, var(--sds-components-font-size-content-sm));

		&:host([variant='tertiary']) {
			--_sds-button-padding-inline: var(--sds-button-padding-inline, 0);
		}
	}

	:host([icon]) {
		--_sds-button-padding-inline: var(--sds-button-padding-inline, 0);
		min-width: var(--_sds-button-min-height);
	}
`;
